import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Edit, Trash2, Eye, Upload, ClipboardList, 
  Calendar, Clock, Search, X, Save, Users, CheckCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeacherAssignments() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCourse, setFilterCourse] = useState('all');
  const [showModal, setShowModal] = useState(searchParams.get('action') === 'add');
  const [editAssignment, setEditAssignment] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    course_id: '',
    subject_id: '',
    due_date: '',
    due_time: '23:59',
    total_marks: 100,
    passing_marks: 40,
    instructions: '',
    attachment_data: '',
    attachment_name: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const getHeaders = () => {
    const token = localStorage.getItem('staffToken');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        navigate('/staff-portal');
        return;
      }

      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [assignmentsRes, coursesRes, subjectsRes] = await Promise.all([
        fetch(`${API_URL}/api/lms/assignments`, { headers }).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/api/lms/teacher/courses`, { headers }).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/api/lms/teacher/subjects`, { headers }).then(r => r.json()).catch(() => [])
      ]);

      setAssignments(Array.isArray(assignmentsRes) ? assignmentsRes : []);
      setCourses(Array.isArray(coursesRes) ? coursesRes : []);
      setSubjects(Array.isArray(subjectsRes) ? subjectsRes : []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    try {
      const { readFileSmart } = await import('@/utils/imageCompressor');
      const result = await readFileSmart(file);
      setFormData(prev => ({
        ...prev,
        attachment_data: result.data,
        attachment_name: file.name
      }));
      toast.success('File attached');
    } catch (err) {
      console.error('File read failed:', err);
      toast.error('Failed to read file');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.course_id || !formData.due_date) {
      toast.error('Please fill required fields');
      return;
    }

    setSaving(true);
    try {
      const url = editAssignment 
        ? `${API_URL}/api/lms/assignments/${editAssignment.id}`
        : `${API_URL}/api/lms/assignments`;
      
      const method = editAssignment ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editAssignment ? 'Assignment updated!' : 'Assignment created!');
        setShowModal(false);
        resetForm();
        fetchData();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save assignment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this assignment?')) return;

    try {
      const response = await fetch(`${API_URL}/api/lms/assignments/${id}`, {
        method: 'DELETE',
        headers: getHeaders()
      });

      if (response.ok) {
        toast.success('Assignment deleted!');
        fetchData();
      }
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', description: '', course_id: '', subject_id: '',
      due_date: '', due_time: '23:59', total_marks: 100, passing_marks: 40,
      instructions: '', attachment_data: '', attachment_name: ''
    });
    setEditAssignment(null);
  };

  const openEdit = (assignment) => {
    setEditAssignment(assignment);
    setFormData({
      title: assignment.title || '',
      description: assignment.description || '',
      course_id: assignment.course_id || '',
      subject_id: assignment.subject_id || '',
      due_date: assignment.due_date || '',
      due_time: assignment.due_time || '23:59',
      total_marks: assignment.total_marks || 100,
      passing_marks: assignment.passing_marks || 40,
      instructions: assignment.instructions || '',
      attachment_data: '',
      attachment_name: assignment.attachment_name || ''
    });
    setShowModal(true);
  };

  const getStatusBadge = (assignment) => {
    const now = new Date();
    const due = new Date(`${assignment.due_date}T${assignment.due_time || '23:59'}`);
    
    if (due < now) {
      return <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded">Closed</span>;
    }
    return <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">Active</span>;
  };

  const filteredAssignments = assignments.filter(a => {
    const matchSearch = a.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCourse = filterCourse === 'all' || a.course_id === filterCourse;
    return matchSearch && matchCourse;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/staff-portal/lms" className="p-2 hover:bg-indigo-600 rounded-lg">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold">Assignments</h1>
                <p className="text-indigo-200 text-sm">Create and manage assignments</p>
              </div>
            </div>
            <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-white text-indigo-700 hover:bg-indigo-50">
              <Plus className="h-4 w-4 mr-2" />
              Create Assignment
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input placeholder="Search assignments..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
              </div>
              <Select value={filterCourse} onValueChange={setFilterCourse}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="All Courses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Courses</SelectItem>
                  {courses.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Assignments List */}
        <div className="space-y-4">
          {filteredAssignments.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <ClipboardList className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No assignments found. Create your first assignment!</p>
              </CardContent>
            </Card>
          ) : (
            filteredAssignments.map(assignment => (
              <Card key={assignment.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-green-100 rounded-lg">
                        <ClipboardList className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{assignment.title}</h3>
                          {getStatusBadge(assignment)}
                        </div>
                        <p className="text-sm text-gray-500">{assignment.course_name}</p>
                        {assignment.description && (
                          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{assignment.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            Due: {assignment.due_date}
                          </span>
                          <span className="flex items-center gap-1">
                            <CheckCircle className="h-4 w-4" />
                            {assignment.total_marks} marks
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {assignment.submission_count || 0} submissions
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Link to={`/staff-portal/lms/submissions?assignment_id=${assignment.id}`}>
                        <Button variant="outline" size="sm">
                          <Eye className="h-4 w-4 mr-1" />
                          View Submissions
                        </Button>
                      </Link>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(assignment)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(assignment.id)} className="text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editAssignment ? 'Edit Assignment' : 'Create Assignment'}</h2>
              <button onClick={() => { setShowModal(false); resetForm(); }}>
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <Label>Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} required />
              </div>
              
              <div>
                <Label>Description</Label>
                <textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" rows={3} />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Course *</Label>
                  <Select value={formData.course_id} onValueChange={(v) => setFormData(prev => ({ ...prev, course_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {courses.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Subject</Label>
                  <Select value={formData.subject_id} onValueChange={(v) => setFormData(prev => ({ ...prev, subject_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {subjects.map(s => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Due Date *</Label>
                  <Input type="date" value={formData.due_date} onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))} required />
                </div>
                <div>
                  <Label>Due Time</Label>
                  <Input type="time" value={formData.due_time} onChange={(e) => setFormData(prev => ({ ...prev, due_time: e.target.value }))} />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Total Marks</Label>
                  <Input type="number" value={formData.total_marks} onChange={(e) => setFormData(prev => ({ ...prev, total_marks: parseInt(e.target.value) || 0 }))} />
                </div>
                <div>
                  <Label>Passing Marks</Label>
                  <Input type="number" value={formData.passing_marks} onChange={(e) => setFormData(prev => ({ ...prev, passing_marks: parseInt(e.target.value) || 0 }))} />
                </div>
              </div>
              
              <div>
                <Label>Instructions</Label>
                <textarea value={formData.instructions} onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))} placeholder="Additional instructions for students..." className="w-full px-3 py-2 border rounded-lg text-sm" rows={3} />
              </div>
              
              <div>
                <Label>Attachment</Label>
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} className="w-full">
                  <Upload className="h-4 w-4 mr-2" />
                  {formData.attachment_name || 'Attach File (Optional)'}
                </Button>
              </div>
              
              <div className="flex gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }} className="flex-1">Cancel</Button>
                <Button type="submit" disabled={saving} className="flex-1">
                  {saving ? 'Saving...' : <><Save className="h-4 w-4 mr-2" />{editAssignment ? 'Update' : 'Create'}</>}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
